import {
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import LabeledBox from '../../special/LabeledBox';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../../global/theme/tokens';
import useFetch from '../../../api/useFetch';

const TypeBookingCategories = ({
  field,
  dataCollection,
  updateValidationOnChange,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  const { apiData: categoriesData } = useFetch('calendar-event-categories');
  const { apiData: usersData } = useFetch('users');

  const categories = dataCollection[field.key] || [];

  function updateCategories(newCategories) {
    updateValidationOnChange(field.key, newCategories);
  }

  function handleAdd() {
    const newCategories = [
      ...categories,
      {
        calendarEventCategoryId: '',
        label: '',
        userIds: [],
        sortOrder: categories.length,
      },
    ];
    updateCategories(newCategories);
  }

  function handleRemove(index) {
    const newCategories = categories.filter((_, i) => i !== index);
    updateCategories(newCategories);
  }

  function handleChange(index, key, value) {
    const newCategories = [...categories];
    newCategories[index] = { ...newCategories[index], [key]: value };

    // Auto-fill label from CalendarEventCategory when selecting a category
    if (key === 'calendarEventCategoryId' && categoriesData?.data) {
      const selectedCat = categoriesData.data.find((c) => c.id === value);
      if (selectedCat && !newCategories[index].label) {
        newCategories[index].label = selectedCat.label;
      }
    }

    updateCategories(newCategories);
  }

  const eventCategories = categoriesData?.data || [];
  const users = usersData?.data || [];

  return (
    <LabeledBox
      label={field.label}
      sx={{ padding: '10px 0px', cursor: 'unset!important' }}
    >
      <Box className="flex flex-col gap-3">
        {categories.map((cat, index) => (
          <Box
            key={index}
            className="flex flex-col gap-2 p-3 rounded-lg"
            sx={{
              bgcolor: colors.bgInput,
              border: `1px solid ${colors.selected}`,
            }}
          >
            <Box className="flex items-center justify-between">
              <Typography variant="subtitle2" fontWeight={600}>
                {t('Category')} {index + 1}
              </Typography>
              <IconButton
                size="small"
                onClick={() => handleRemove(index)}
                color="error"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* CalendarEventCategory Selection */}
            <Select
              value={cat.calendarEventCategoryId || ''}
              onChange={(e) =>
                handleChange(index, 'calendarEventCategoryId', e.target.value)
              }
              size="small"
              displayEmpty
              fullWidth
            >
              <MenuItem value="" disabled>
                {t('Select Event Category')}
              </MenuItem>
              {eventCategories.map((ec) => (
                <MenuItem key={ec.id} value={ec.id}>
                  <Box className="flex items-center gap-2">
                    {ec.color && (
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          bgcolor: ec.color,
                        }}
                      />
                    )}
                    {ec.label}
                  </Box>
                </MenuItem>
              ))}
            </Select>

            {/* Customer-facing label */}
            <TextField
              label={t('Customer Label')}
              value={cat.label || ''}
              onChange={(e) => handleChange(index, 'label', e.target.value)}
              size="small"
              fullWidth
            />

            {/* User assignment (multi-select) */}
            <Select
              multiple
              value={cat.userIds || []}
              onChange={(e) =>
                handleChange(index, 'userIds', e.target.value)
              }
              size="small"
              displayEmpty
              fullWidth
              renderValue={(selected) => {
                if (!selected || selected.length === 0) {
                  return (
                    <Typography variant="body2" sx={{ opacity: 0.5 }}>
                      {t('Select Staff')}
                    </Typography>
                  );
                }
                return (
                  <Box className="flex flex-wrap gap-1">
                    {selected.map((userId) => {
                      const user = users.find((u) => u.id === userId);
                      return (
                        <Chip
                          key={userId}
                          label={
                            user
                              ? `${user.firstName} ${user.lastName}`
                              : userId
                          }
                          size="small"
                        />
                      );
                    })}
                  </Box>
                );
              }}
            >
              {users.map((user) => (
                <MenuItem key={user.id} value={user.id}>
                  {user.firstName} {user.lastName}
                </MenuItem>
              ))}
            </Select>
          </Box>
        ))}

        <Button
          size="small"
          color="secondary"
          onClick={handleAdd}
          sx={{ alignSelf: 'flex-start', paddingLeft: '2px' }}
        >
          <AddCircleIcon color="success" fontSize="small" sx={{ mr: 0.5 }} />
          {t('Add Category')}
        </Button>
      </Box>
    </LabeledBox>
  );
};

export default TypeBookingCategories;
